import React, { useState } from 'react';
import { Link, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { SignupData } from '../types';
import { EMPLOYEE_COUNT_OPTIONS, BUSINESS_TYPE_OPTIONS } from '../utils/profileOptions';
import LoadingSpinner from '../components/LoadingSpinner';
import { trackEvent } from '../utils/analytics';
import { buildBackendUrl } from '../services/api';

const Signup: React.FC = () => {
  const { signup, loading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const stateReturnTo = (location.state as { returnTo?: string } | null)?.returnTo ?? '';
  const requestedReturnTo = searchParams.get('returnTo') ?? stateReturnTo;
  const returnTo = requestedReturnTo.startsWith('/') && !requestedReturnTo.startsWith('//') ? requestedReturnTo : '';
  const [formData, setFormData] = useState<SignupData>({
    email: '',
    password: '',
    confirm_password: '',
    full_name: '',
    business_name: '',
    employee_count: '',
    business_type: '',
    phone: '',
    city: '',
    company_website: '',
  });
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setIsSubmitting(true);

    if (formData.password !== formData.confirm_password) {
      setError('Passwords do not match.');
      setIsSubmitting(false);
      return;
    }

    try {
      await signup(formData);
      navigate(returnTo || '/home');
    } catch (err: any) {
      setError(err.response?.data?.error || 'Signup failed. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (loading) {
    return <LoadingSpinner message="Loading..." />;
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div>
          <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
            Create your account
          </h2>
          <p className="mt-2 text-center text-sm text-gray-600">
            Start with 30 days of Pro, free. No card required; access ends automatically.
          </p>
          <p className="mt-2 text-center text-sm text-gray-600">
            Or{' '}
            <Link
              to={returnTo ? `/login?returnTo=${encodeURIComponent(returnTo)}` : '/login'}
              className="font-medium text-blue-600 hover:text-blue-500"
            >
              sign in to your existing account
            </Link>
          </p>
        </div>

        <button
          type="button"
          onClick={() => {
            trackEvent('sign_up_start', { method: 'google' });
            const next = returnTo ? `?next=${encodeURIComponent(returnTo)}` : '';
            window.location.href = buildBackendUrl(`/auth/google${next}`);
          }}
          className="flex w-full items-center justify-center gap-2 rounded-lg border border-gray-200 bg-white px-4 py-2 text-sm font-semibold text-gray-700 shadow-sm transition hover:bg-gray-50"
        >
          <GoogleIcon />
          Continue with Google
        </button>

        <div className="flex items-center gap-3 text-xs text-gray-400">
          <div className="flex-1 border-t border-gray-200" />
          <span>or sign up with email</span>
          <div className="flex-1 border-t border-gray-200" />
        </div>

        <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
          <div className="hidden" aria-hidden>
            <label htmlFor="company_website" className="sr-only">Company Website</label>
            <input
              id="company_website"
              name="company_website"
              type="text"
              tabIndex={-1}
              autoComplete="off"
              value={formData.company_website}
              onChange={handleChange}
            />
          </div>

          {error && (
            <div className="rounded-md bg-red-50 p-4">
              <div className="text-sm text-red-700">{error}</div>
            </div>
          )}
          
          <div className="space-y-4">
            <div>
              <label htmlFor="full_name" className="block text-sm font-medium text-gray-700">
                Full Name *
              </label>
              <input
                id="full_name"
                name="full_name"
                type="text"
                required
                className="input-field mt-1"
                placeholder="Enter your full name"
                value={formData.full_name}
                onChange={handleChange}
              />
            </div>

            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                Email Address *
              </label>
              <input
                id="email"
                name="email"
                type="email"
                autoComplete="email"
                required
                className="input-field mt-1"
                placeholder="Enter your email"
                value={formData.email}
                onChange={handleChange}
              />
            </div>

            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-700">
                Password *
              </label>
              <input
                id="password"
                name="password"
                type="password"
                required
                className="input-field mt-1"
                placeholder="Create a password"
                value={formData.password}
                onChange={handleChange}
              />
            </div>

            <div>
              <label htmlFor="confirm_password" className="block text-sm font-medium text-gray-700">
                Confirm Password *
              </label>
              <input
                id="confirm_password"
                name="confirm_password"
                type="password"
                required
                className="input-field mt-1"
                placeholder="Confirm your password"
                value={formData.confirm_password}
                onChange={handleChange}
              />
            </div>

            <div>
              <label htmlFor="business_name" className="block text-sm font-medium text-gray-700">
                Business Name
              </label>
              <input
                id="business_name"
                name="business_name"
                type="text"
                className="input-field mt-1"
                placeholder="Enter your business name"
                value={formData.business_name}
                onChange={handleChange}
              />
            </div>

            <div>
              <label htmlFor="employee_count" className="block text-sm font-medium text-gray-700">
                Number of Employees *
              </label>
              <select
                id="employee_count"
                name="employee_count"
                required
                className="input-field mt-1"
                value={formData.employee_count}
                onChange={handleChange}
              >
                <option value="">Select employee count</option>
                {EMPLOYEE_COUNT_OPTIONS.map((option) => (
                  <option key={option} value={option}>
                    {option}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label htmlFor="business_type" className="block text-sm font-medium text-gray-700">
                Business Type
              </label>
              <select
                id="business_type"
                name="business_type"
                className="input-field mt-1"
                value={formData.business_type}
                onChange={handleChange}
              >
                <option value="">Select a business type</option>
                {BUSINESS_TYPE_OPTIONS.map((option) => (
                  <option key={option} value={option}>
                    {option}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label htmlFor="phone" className="block text-sm font-medium text-gray-700">
                Phone Number
              </label>
              <input
                id="phone"
                name="phone"
                type="tel"
                className="input-field mt-1"
                placeholder="Enter your phone number"
                value={formData.phone}
                onChange={handleChange}
              />
            </div>

            <div>
              <label htmlFor="city" className="block text-sm font-medium text-gray-700">
                City
              </label>
              <input
                id="city"
                name="city"
                type="text"
                className="input-field mt-1"
                placeholder="Enter your city"
                value={formData.city}
                onChange={handleChange}
              />
            </div>
          </div>

          <div>
            <button
              type="submit"
              disabled={isSubmitting}
              className="group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSubmitting ? (
                <div className="flex items-center">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Creating account...
                </div>
              ) : (
                'Create Account'
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default Signup;

const GoogleIcon: React.FC = () => (
  <svg role="img" aria-hidden className="h-5 w-5" viewBox="0 0 24 24">
    <path fill="#4285F4" d="M23.49 12.27c0-.82-.07-1.64-.22-2.44H12v4.62h6.48a5.54 5.54 0 0 1-2.39 3.63v3h3.86c2.26-2.08 3.54-5.15 3.54-8.81z" />
    <path fill="#34A853" d="M12 24c3.22 0 5.93-1.07 7.9-2.92l-3.86-3a7.43 7.43 0 0 1-4.04 1.16c-3.1 0-5.73-2.09-6.67-4.9H1.37v3.09A12 12 0 0 0 12 24z" />
    <path fill="#FBBC05" d="M5.33 14.34a7.2 7.2 0 0 1 0-4.68V6.57H1.37a12 12 0 0 0 0 10.86l3.96-3.09z" />
    <path fill="#EA4335" d="M12 4.75c1.75 0 3.32.6 4.55 1.78l3.41-3.41C17.93 1.13 15.22 0 12 0A12 12 0 0 0 1.37 6.57l3.96 3.09C5.71 7.84 8.34 4.75 12 4.75z" />
  </svg>
);
